import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { Panel, StatStrip, Sw, Empty, CellSub } from "@/components/portal/crm/kit";

const SOURCE = { hub: "From Ask Connie", weekly: "Monday tidy-up" };

/** What Connie may change, the Monday tidy-up, and every message she has sent partners. */
export function ConniePowers() {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD((await api.get("/hub/connie/powers")).data); } catch { toast.error("Couldn't load Connie's powers"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flip = async (key) => {
    try {
      await api.put("/hub/connie/settings", { powers: { ...d.powers, [key]: !d.powers[key] } });
      toast.success(d.powers[key] ? "Switched off — she stops straight away" : "Switched on");
      load();
    } catch { toast.error("That didn't save"); }
  };

  if (!d) return <Empty testId="hub-connie-powers-loading">Loading…</Empty>;
  return (
    <div className="space-y-5" data-testid="hub-connie-powers">
      <StatStrip testId="hub-connie-powers-stats" items={[
        { k: "Messaged this week", v: d.counts.messaged_week, h: "partners, in their portal", testId: "hub-connie-powers-messaged" },
        { k: "Changes made", v: d.counts.changes_week, h: "last 7 days, on request", testId: "hub-connie-powers-changes" },
        { k: "Missing something", v: d.counts.partners_missing, h: "live partners", testId: "hub-connie-powers-missing" },
        { k: "Team jobs", v: d.counts.team_jobs, h: "paperwork still to issue", testId: "hub-connie-powers-team" },
      ]} />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-start">
        <PowerSwitches d={d} onFlip={flip} />
        <div className="space-y-5">
          <Tidy d={d} onRan={load} />
          <MorningText on={d.powers.owner_text} />
        </div>
      </div>
      <Sent rows={d.recent} />
    </div>
  );
}

function PowerSwitches({ d, onFlip }) {
  return (
    <Panel title="What she can change" pad={false} testId="hub-connie-power-list"
           sub="Anything a partner asks her to change, she checks first and only acts on their yes. Group messages always come back to you before they go.">
      {Object.entries(d.labels).map(([key, label]) => {
        const [title, sub] = label.split(" — ");
        return (
          <div key={key} style={{ padding: "15px 22px", borderBottom: "1px solid var(--line-soft)", display: "flex", alignItems: "center", gap: 16 }}
               data-testid={`hub-connie-power-row-${key}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{title}</div>
              {sub && <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 2 }}>{sub}</div>}
            </div>
            <Sw on={d.powers[key]} disabled={!d.enabled} onClick={() => onFlip(key)} testId={`hub-connie-power-${key}`} />
          </div>
        );
      })}
      {!d.enabled && <CellSub style={{ padding: "12px 22px" }}>Connie is paused everywhere, so none of these run until she&apos;s back on.</CellSub>}
    </Panel>
  );
}

function Tidy({ d, onRan }) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const due = d.tidy.due;
  const run = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/hub/connie/tidy/run")).data;
      toast.success(`Connie is writing to ${r.queued} partner${r.queued === 1 ? "" : "s"} now`);
      setAsk(false);
      setTimeout(onRan, 4000);
    } catch { toast.error("Couldn't start the tidy-up"); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Monday tidy-up" testId="hub-connie-tidy"
           sub={d.powers.weekly_tidy ? `Next run ${d.tidy.next_run}. Anyone still missing something gets one personal message — never more than once a week.` : "Switched off — nobody gets the Monday message until you turn it back on."}>
      {due.length === 0 ? <CellSub data-testid="hub-connie-tidy-empty">Nobody is due a message — everyone is either up to date or heard from her this week.</CellSub> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 360, overflowY: "auto" }} data-testid="hub-connie-tidy-due">
          {due.map((r) => (
            <div key={r.partner_id} style={{ border: "1px solid var(--line)", borderRadius: 12, padding: "11px 14px" }} data-testid={`hub-connie-tidy-row-${r.partner_id}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <b style={{ fontSize: 13.5 }} className="truncate">{r.name}</b>
                <span style={{ fontSize: 11.5, color: "var(--mut)" }}>{r.type}</span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--mut)", whiteSpace: "nowrap" }}>
                  {r.last_messaged_at ? `Last messaged ${ukDay(r.last_messaged_at)}` : "Never messaged"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
                {r.missing.map((m) => <span key={m.code} className="st st-sent" style={{ height: 22, fontSize: 11 }}><i className="sq" />{m.label}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
      {d.tidy.recently_messaged.length > 0 && (
        <CellSub style={{ marginTop: 12 }} data-testid="hub-connie-tidy-recent">
          {d.tidy.recently_messaged.length} more heard from her in the last week, so she&apos;ll leave them be until next time.
        </CellSub>
      )}
      {due.length > 0 && (
        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {!ask ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setAsk(true)} data-testid="hub-connie-tidy-run">
              <Send /> Run it now
            </button>
          ) : (
            <>
              <span style={{ fontSize: 12.5 }}>Message {due.length} partner{due.length === 1 ? "" : "s"} now?</span>
              <button className="btn btn-primary btn-sm" onClick={run} disabled={busy} data-testid="hub-connie-tidy-confirm">
                {busy ? <Loader2 className="animate-spin" /> : <Send />} Yes, send
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAsk(false)} data-testid="hub-connie-tidy-cancel">Cancel</button>
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

function MorningText({ on }) {
  const [st, setSt] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [ask, setAsk] = useState(false);
  const load = useCallback(async () => {
    try { setSt((await api.get("/hub/connie/morning-text")).data); } catch { /* panel stays quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const write = async () => {
    setBusy("preview");
    try { setPreview((await api.post("/hub/connie/morning-text/preview")).data.text); }
    catch { toast.error("Connie couldn't write it just now"); }
    finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("send");
    try {
      const r = (await api.post("/hub/connie/morning-text/send")).data;
      if (r.sent) { toast.success(`Texted to ${r.to}`); setPreview(r.text); } else toast.error("The text didn't go — check the desk-alerts mobile");
      setAsk(false); load();
    } catch { toast.error("The text didn't go"); }
    finally { setBusy(""); }
  };
  const shown = preview || st?.last?.text;
  return (
    <Panel title="Owner morning text" testId="hub-connie-morning"
           sub={st?.to ? `${on ? "On" : "Off"} — ${st.hour_uk}am UK, Monday to Friday, to the desk-alerts mobile ending ${st.to}.` : "Add a desk-alerts mobile (ADMIN_SMS_MOBILE) and Connie texts you at 8am on weekdays."}>
      {shown ? (
        <div style={{ background: "var(--paper-2, #F4F6FA)", borderRadius: 14, padding: "12px 14px", fontSize: 13, lineHeight: 1.55 }}>
          <p style={{ margin: 0 }} data-testid="hub-connie-morning-text">{shown}</p>
          <div style={{ fontSize: 11, color: "var(--mut)", marginTop: 6 }} data-testid="hub-connie-morning-when">
            {preview ? "Preview — written just now, not sent" : `Sent ${ukDay(st.last.at)} · ${ukTime(st.last.at)}`}
          </div>
        </div>
      ) : <CellSub data-testid="hub-connie-morning-empty">Nothing sent yet — preview this morning&apos;s to see what she&apos;d say.</CellSub>}
      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn btn-ghost btn-sm" onClick={write} disabled={!!busy} data-testid="hub-connie-morning-preview">
          {busy === "preview" ? <Loader2 className="animate-spin" /> : null} Preview today&apos;s
        </button>
        {st?.to && (!ask ? (
          <button className="btn btn-ghost btn-sm" onClick={() => setAsk(true)} data-testid="hub-connie-morning-send">
            <Send /> Text it to me now
          </button>
        ) : (
          <>
            <button className="btn btn-primary btn-sm" onClick={send} disabled={!!busy} data-testid="hub-connie-morning-send-confirm">
              {busy === "send" ? <Loader2 className="animate-spin" /> : <Send />} Yes, text {st.to}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setAsk(false)} data-testid="hub-connie-morning-send-cancel">Cancel</button>
          </>
        ))}
      </div>
      <OwnerThread st={st} />
    </Panel>
  );
}

function OwnerThread({ st }) {
  if (!st?.reply_number) return null;
  const rows = st.thread || [];
  return (
    <div style={{ marginTop: 18, borderTop: "1px solid var(--line-soft)", paddingTop: 14 }} data-testid="hub-connie-owner-thread">
      <div style={{ fontWeight: 700, fontSize: 13 }}>Text her a job</div>
      <CellSub style={{ marginTop: 2 }} data-testid="hub-connie-owner-thread-sub">
        {!st.two_way
          ? <>Your desk-alerts mobile (ending {st.to}) isn&apos;t a UK number, and the UK reply number {st.reply_number} can only text back and forth with UK mobiles — so your morning text comes from the usual sender and replies can&apos;t reach her. Use a UK mobile for desk alerts to text her jobs.</>
          : st.replies_on
          ? <>Reply to her morning text, or text <b data-testid="hub-connie-owner-reply-number">{st.reply_number}</b> any time from the mobile ending {st.to}: &ldquo;chase the quiet desks&rdquo;, &ldquo;what&apos;s on CX-…?&rdquo;. Chases go straight out; anything to partners or clients waits for your YES.</>
          : <>Switched off — turn on &ldquo;Text her jobs&rdquo; above and she&apos;ll act on your replies.</>}
      </CellSub>
      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, maxHeight: 340, overflowY: "auto" }} data-testid="hub-connie-owner-thread-list">
          {rows.map((m) => <Bubble key={m.id} m={m} />)}
        </div>
      )}
    </div>
  );
}

function Bubble({ m }) {
  const you = m.role === "owner";
  return (
    <div style={{ alignSelf: you ? "flex-end" : "flex-start", maxWidth: "86%" }} data-testid={`hub-connie-owner-msg-${m.id}`}>
      <div style={{ background: you ? "var(--ink, #0B1220)" : "var(--paper-2, #F4F6FA)", color: you ? "#fff" : "inherit",
                    borderRadius: 14, padding: "8px 12px", fontSize: 12.5, lineHeight: 1.5 }}>{m.text}</div>
      <div style={{ fontSize: 10.5, color: "var(--mut)", marginTop: 3, textAlign: you ? "right" : "left" }}>
        {you ? "You" : m.kind === "morning" ? "Connie · 8am text" : "Connie"} · {ukDay(m.at)} {ukTime(m.at)}
      </div>
    </div>
  );
}

function Sent({ rows }) {
  return (
    <Panel title="Messages she's sent partners" sub="Every message Connie has put in a partner's portal, newest first" pad={false} testId="hub-connie-outreach">
      {rows.length === 0 ? <Empty testId="hub-connie-outreach-empty">Nothing yet. Ask her: &ldquo;Who&apos;s missing paperwork or a mobile?&rdquo;</Empty> : (
        <div style={{ maxHeight: 520, overflowY: "auto" }}>
          {rows.map((o) => (
            <div key={o.id} style={{ padding: "13px 22px", borderBottom: "1px solid var(--line-soft)" }} data-testid={`hub-connie-outreach-${o.id}`}>
              <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                <b style={{ fontSize: 13 }}>{o.party_name}</b>
                <span style={{ fontSize: 11.5, color: "var(--mut)" }}>{SOURCE[o.source] || o.source}{o.channel === "email" ? " · by email" : ""}</span>
                <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--mut)", whiteSpace: "nowrap" }}>{ukDay(o.at)} · {ukTime(o.at)}</span>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-3)", marginTop: 5, whiteSpace: "pre-line" }} className="line-clamp-3">{o.text}</div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
